import "@/App.css";
import { Suspense, lazy, useEffect, useRef } from "react";
import { BrowserRouter, Link, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { DirectionProvider } from "@radix-ui/react-direction";
import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import { LanguageProvider, useLang } from "@/lib/i18n";
import { PreferencesProvider, usePreferences } from "@/lib/preferences";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { FullScreenLoader, RequireAuth } from "@/components/auth/RequireAuth";
import LandingPage from "@/pages/LandingPage";
import { MANAGER_OR_ADMIN, SHELL_ROUTES } from "@/routes";

// Phase 56 (committee R-5): everything behind the landing page is code-split; the landing itself stays in the first chunk.
const AppShell = lazy(() => import("@/components/layout/AppShell"));
const AuthPage = lazy(() => import("@/pages/AuthPage"));
const SetPasswordPage = lazy(() => import("@/pages/SetPasswordPage"));
const LegalPage = lazy(() => import("@/pages/LegalPage"));
const ModelPage = lazy(() => import("@/pages/ModelPage"));
const PricingPage = lazy(() => import("@/pages/PricingPage"));

/**
 * `/` and `/ar` (committee R-14): the Arabic landing page has its own address for search engines and sharing; `/?lang=ar`
 * works too. Shared calculator scenarios (`/?estimate=ID`) now open on /model; signed-in managers still land on the
 * draft-holding page for that estimate.
 */
function HomeRoute({ forceLang }) {
  const { user, loading } = useAuth();
  const { lang, setLang } = useLang();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const wanted = forceLang || params.get("lang");
  const estimate = params.get("estimate");
  const showingLanding = !loading && !user;
  // Apply the address's language once per visit to that address (a ref, not a live constraint): the header toggle flips the
  // language and moves to the other address in the same click, and a live constraint would snap the language back.
  const applied = useRef(null);
  useEffect(() => {
    const target = wanted === "ar" || wanted === "en" ? wanted : null;
    if (!target) { applied.current = null; return; }
    if (!showingLanding || applied.current === target) return;
    applied.current = target;
    if (target !== lang) setLang(target);
  }, [showingLanding, wanted, lang, setLang]);
  if (loading) return <FullScreenLoader />;
  if (user) {
    if (user.must_change_password) return <Navigate to="/set-password" replace />;
    if (estimate && MANAGER_OR_ADMIN.includes(user.role)) return <Navigate to={`/pools/from-estimate/${estimate}`} replace />;
    return <Navigate to="/dashboard" replace />;
  }
  if (estimate) return <Navigate to={`/model?estimate=${encodeURIComponent(estimate)}`} replace />;
  return <LandingPage />;
}

function NotFound() {
  const { t } = useLang();
  return (
    <div className="rounded-xl border border-border bg-card p-10 text-center">
      <h2 className="text-lg font-semibold">{t("not_found")}</h2>
      <Button asChild className="mt-4"><Link to="/dashboard">{t("go_home")}</Link></Button>
    </div>
  );
}

/** One <Route/> per SHELL_ROUTES entry; role-limited pages are wrapped in <RequireAuth roles/>. */
const shellRoute = ({ path, Page, roles }) => (
  <Route key={path} path={path} element={roles ? <RequireAuth roles={roles}><Page /></RequireAuth> : <Page />} />
);

/** Everything that translates with t() waits for the active dictionary (lib/i18n.js loads it on demand); the landing page does not need it. */
function WhenTranslationsReady({ children }) {
  const { ready } = useLang();
  return ready ? children : <FullScreenLoader />;
}

function Shell() {
  const { dir } = useLang();
  const { resolvedTheme } = usePreferences();
  return (
    <DirectionProvider dir={dir}>
      <TooltipProvider delayDuration={200}>
        <BrowserRouter>
          <Suspense fallback={<FullScreenLoader />}>
            <Routes>
              <Route path="/" element={<HomeRoute />} />
              <Route path="/ar" element={<HomeRoute forceLang="ar" />} />
              {/* Phase 56: the holding model has its own public page; shared scenarios open here (/model?estimate=ID). */}
              <Route path="/model" element={<WhenTranslationsReady><ModelPage /></WhenTranslationsReady>} />
              {/* Public pricing page (owner decision): plans and fixed per-matter fees from the published schedule. */}
              <Route path="/pricing" element={<PricingPage />} />
              <Route path="/auth" element={<WhenTranslationsReady><AuthPage /></WhenTranslationsReady>} />
              {/* Typed-URL habits: /login and /signup are the sign-in page too (otherwise the deep-link return lands on "Page not found"). */}
              <Route path="/login" element={<Navigate to="/auth" replace />} />
              <Route path="/signup" element={<Navigate to="/auth?mode=register" replace />} />
              {/* Phase 41: public legal documents (EN/AR); /terms and /privacy are the habitual short URLs. */}
              <Route path="/legal/:doc" element={<WhenTranslationsReady><LegalPage /></WhenTranslationsReady>} />
              <Route path="/legal" element={<Navigate to="/legal/terms" replace />} />
              <Route path="/terms" element={<Navigate to="/legal/terms" replace />} />
              <Route path="/privacy" element={<Navigate to="/legal/privacy" replace />} />
              <Route path="/set-password" element={<WhenTranslationsReady><RequireAuth><SetPasswordPage /></RequireAuth></WhenTranslationsReady>} />
              <Route element={<WhenTranslationsReady><RequireAuth><AppShell /></RequireAuth></WhenTranslationsReady>}>
                {SHELL_ROUTES.map(shellRoute)}
                <Route path="*" element={<NotFound />} />
              </Route>
            </Routes>
          </Suspense>
        </BrowserRouter>
        <Toaster position="top-center" richColors closeButton theme={resolvedTheme} dir={dir} />
      </TooltipProvider>
    </DirectionProvider>
  );
}

export default function App() {
  return (
    <div className="App">
      <LanguageProvider>
        <AuthProvider>
          <PreferencesProvider>
            <Shell />
          </PreferencesProvider>
        </AuthProvider>
      </LanguageProvider>
    </div>
  );
}
